import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { logoImg, googleIcon } from '../../utils/images';
import '../../components/LoginSupabase/LoginS.scss';
import './SignUpPage.scss';

export const SignUpPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const trimmedEmail = email.trim();
      const { error } = await supabase.auth.signUp({
        email: trimmedEmail,
        password,
      });
      if (error) throw error;
      navigate(`/email-otp?flow=signup&email=${encodeURIComponent(trimmedEmail)}`);
    } catch (err: any) {
      setError(err.message || 'Sign up failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/stores`,
        },
      });
      if (error) throw error;
    } catch (err: any) {
      setError(err.message || 'Google sign up failed.');
      setIsLoading(false);
    }
  };

  return (
    <div className="signUpPage">
      <div className="login">
        <div className="login__container">
          <div className="login__t">
            <div className="login__logo-wrapper">
              <img src={logoImg} alt="Dovery" className="login__logo" />
            </div>

            <form className="login-form" onSubmit={handleSignUp}>
                <div className="login-form__field">
                  <span className="login-form__icon">👤</span>
                  <input
                    type="email"
                    className="login-form__input"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>

                <div className="login-form__field">
                  <span className="login-form__icon">🔒</span>
                  <input
                    type="password"
                    className="login-form__input"
                    placeholder="Password (min. 8 characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>

                <div className="login-form__field login-form__field--last">
                  <span className="login-form__icon">🔒</span>
                  <input
                    type="password"
                    className="login-form__input"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>

                {error && <p className="login-form__error">{error}</p>}

                <button
                  type="submit"
                  className="login-form__submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating account...' : 'Create Account'}
                </button>

                <p className="auth-form__description sign-up-page__otp-note">
                  We will send a one-time verification code to your email.
                </p>

                <div className="login-form__divider">
                  <span className="login-form__divider-text">- - or - -</span>
                </div>

                <button
                  type="button"
                  className="login-form__google"
                  onClick={handleGoogleSignUp}
                  disabled={isLoading}
                >
                  <img src={googleIcon} alt="Google" className="login-form__google-icon" />
                  Sign up with Google
                </button>

                <p className="sign-up-page__login-link">
                  Already have an account?{' '}
                  <Link to="/">Log in</Link>
                </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
